// models/Message.js

class Message {
    constructor(data = {}) {
      this.id = data.id || data._id || null;
      this._id = data._id || data.id || null;
      this.messageId = data.messageId || '';
      this.conversationId = data.conversationId || '';
      this.senderId = data.senderId || '';
      this.senderName = data.senderName || '';
      this.senderAvatar = data.senderAvatar || '';
      this.recipientId = data.recipientId || '';
      this.type = data.type || 'text'; // text, file, image, video, audio
      this.text = data.text || '';
      this.file = data.file || null;
      this.status = data.status || 'sending'; // sending, sent, delivered, read, failed
      this.isRead = data.isRead || false;
      this.readAt = data.readAt || null;
      this.deliveredAt = data.deliveredAt || null;
      this.isEdited = data.isEdited || false;
      this.editedAt = data.editedAt || null;
      this.isDeleted = data.isDeleted || false;
      this.deletedAt = data.deletedAt || null;
      this.replyTo = data.replyTo || null;
      this.timestamp = data.timestamp || new Date();
      this.createdAt = data.createdAt || new Date();
      this.updatedAt = data.updatedAt || null;
    }
  
    // Validation
    validate() {
      const errors = [];
  
      if (!this.conversationId) {
        errors.push('Conversation ID is required');
      }
  
      if (this.type === 'text' && !this.text?.trim()) {
        errors.push('Message text is required');
      }
  
      if (this.type !== 'text' && !this.file) {
        errors.push('File is required for non-text messages');
      }
  
      if (!['text', 'file', 'image', 'video', 'audio', 'location'].includes(this.type)) {
        errors.push('Invalid message type');
      }
  
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  
    // Convert to JSON for API
    toJSON() {
      return {
        id: this.id,
        conversationId: this.conversationId,
        senderId: this.senderId,
        recipientId: this.recipientId,
        type: this.type,
        text: this.text,
        file: this.file,
        status: this.status,
        isRead: this.isRead,
        replyTo: this.replyTo
      };
    }
  
    // Create from API response
    static fromJSON(json) {
      return new Message({
        id: json._id || json.id,
        messageId: json.messageId,
        conversationId: json.conversationId,
        senderId: json.senderId?._id || json.senderId,
        senderName: json.senderName || json.senderId?.name,
        senderAvatar: json.senderAvatar || json.senderId?.avatar,
        recipientId: json.recipientId,
        type: json.type,
        text: json.text,
        file: json.file,
        status: json.status,
        isRead: json.isRead,
        readAt: json.readAt,
        deliveredAt: json.deliveredAt,
        isEdited: json.isEdited,
        editedAt: json.editedAt,
        isDeleted: json.isDeleted,
        deletedAt: json.deletedAt,
        replyTo: json.replyTo,
        timestamp: json.timestamp || json.createdAt,
        createdAt: json.createdAt,
        updatedAt: json.updatedAt
      });
    }
  
    // Check if message is mine
    isMine(currentUserId) {
      return this.senderId === currentUserId;
    }
  
    // Format timestamp
    getFormattedTime() {
      const date = new Date(this.timestamp);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  
    // Get relative time
    getRelativeTime() {
      const now = new Date();
      const diff = now - new Date(this.timestamp);
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
  
      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return new Date(this.timestamp).toLocaleDateString();
    }
  
    // Check if file is image
    isImage() {
      return this.type === 'image' || this.file?.mimeType?.startsWith('image/');
    }
  
    // Check if file is video
    isVideo() {
      return this.type === 'video' || this.file?.mimeType?.startsWith('video/');
    }
  
    // Get file size in readable format
    getFileSize() {
      if (!this.file?.fileSize) return '';
      const size = this.file.fileSize;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
  }
  
  export default Message;